import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
    Plus, Search, Calendar, RefreshCw, Eye, FileEdit, X, ShoppingCart,
    User, Package, IndianRupee, ArrowLeft,
    Filter
} from "lucide-react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";

const Purchase = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [purchases, setPurchases] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [supplierFilter, setSupplierFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState(''); // State for type
    const [suppliers, setSuppliers] = useState([]); // State for suppliers list
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const isInitialMount = useRef(true);
    const [showFilters, setShowFilters] = useState(false);

    // Read query params on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const typeParam = params.get('type');
        if (typeParam) {
            setTypeFilter(typeParam);
        }

        // Fetch suppliers for filter
        const fetchSuppliers = async () => {
            try {
                const res = await axiosInstance.get('/supplier');
                setSuppliers(res.data);
            } catch (err) {
                console.error("Failed to fetch suppliers", err);
            }
        };
        fetchSuppliers();
    }, [location.search]);

    const fetchPurchases = async (searchQuery = "", supplierQuery = "", dateQuery = "", typeQuery = "") => {
        setLoading(true);
        try {
            let url = `/purchases?page=${page}&limit=${limit}`;
            if (searchQuery.trim()) url += `&search=${encodeURIComponent(searchQuery.trim())}`;
            if (supplierQuery.trim()) url += `&supplier_id=${encodeURIComponent(supplierQuery.trim())}`;
            if (dateQuery.trim()) url += `&date=${encodeURIComponent(dateQuery.trim())}`;
            if (typeQuery || typeFilter) url += `&type=${encodeURIComponent(typeQuery || typeFilter)}`; // Use param or state

            const response = await axiosInstance.get(url);
            const formattedPurchases = response.data.purchases.map(purchase => ({
                ...purchase,
                id: purchase.id,
                SupplierName: purchase.Supplier?.name || '',
                Purchasedate: purchase.purchase_date ? purchase.purchase_date.split('T')[0] : '',
                FormattedAmount: `₹${parseFloat(purchase.total_amount).toFixed(2)}`
            }));
            setPurchases(formattedPurchases);
            setTotal(response.data.total || formattedPurchases.length);
            setError('');
        } catch (error) {
            console.error('Error fetching purchases:', error);
            setError('Failed to load purchases');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isInitialMount.current && page !== 1) {
            setPage(1);
        }
    }, [searchTerm, supplierFilter, dateFilter]);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchPurchases(searchTerm, supplierFilter, dateFilter, typeFilter);
            isInitialMount.current = false;
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [page, searchTerm, supplierFilter, dateFilter, typeFilter]);

    const handleViewClick = async (purchase) => {
        try {
            const res = await axiosInstance.get(`/purchases/${purchase.id}`);
            const data = res.data.purchase;

            setSelectedPurchase({
                ...data,
                Purchasedate: data.purchase_date.split('T')[0],
                FormattedAmount: `₹${parseFloat(data.total_amount).toFixed(2)}`,
                FormattedDiscount: `₹${parseFloat(data.discount_amount || 0).toFixed(2)}`,
                FormattedGst: `₹${parseFloat(data.total_gst_amount || 0).toFixed(2)}`,
            });
            setShowViewModal(true);
        } catch (err) {
            console.error("Error fetching purchase:", err);
            setError("Failed to load purchase details");
        }
    };

    const handleClearFilters = () => {
        setSearchTerm("");
        setSupplierFilter("");
        setDateFilter("");
        setTypeFilter("");
        navigate(location.pathname, { replace: true }); // Clear URL params
        setPage(1);
    };

    const handleFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setPage(1);
    };

    const getStatusColor = (status) => {
        const map = {
            PAID: 'emerald',
            PARTIAL: 'amber',
            PENDING: 'yellow',
            RECEIVED: 'blue',
        };
        return map[status] || 'gray';
    };

    const Badge = ({ color, children }) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-${color}-100 text-${color}-800 border border-${color}-200`}>
            {children}
        </span>
    );

    const InfoCard = ({ icon: Icon, label, value, highlight, className = '' }) => (
        <div className={`p-4 rounded-xl border ${highlight ? `bg-gradient-to-br ${className}` : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${highlight ? 'bg-white/30' : 'bg-gray-200'}`}>
                    <Icon size={18} className={highlight ? 'text-white' : 'text-gray-600'} />
                </div>
                <div>
                    <p className="text-xs text-gray-600">{label}</p>
                    <p className="font-bold text-gray-900">{value}</p>
                </div>
            </div>
        </div>
    );

    const PurchaseViewModal = ({ purchase, onClose }) => {
        if (!purchase) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                <div className="bg-gray-50 rounded-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto shadow-2xl">
                    <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
                        <h2 className="text-xl font-bold text-gray-900">Purchase Details - #{purchase.invoice_number || purchase.id}</h2>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
                    </div>

                    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row items-start gap-4">
                            <button onClick={onClose} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group text-sm">
                                <div className="p-1 rounded-lg group-hover:bg-gray-100"><ArrowLeft className="w-4 h-4" /></div>
                                <span className="font-medium">Back</span>
                            </button>
                            <div className="flex items-start gap-3 sm:gap-4 flex-1">
                                <div className="p-2.5 sm:p-3 bg-teal-600 rounded-2xl shadow-lg">
                                    <ShoppingCart className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                                </div>
                                <div className="flex-1">
                                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1">Purchase #{purchase.invoice_number || purchase.id}</h1>
                                    <p className="text-sm sm:text-base text-gray-600 font-medium">
                                        {new Date(purchase.purchase_date).toLocaleDateString("en-GB")}
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        <Badge color={getStatusColor(purchase.payment_status)}>{purchase.payment_status}</Badge>
                                        {purchase.payment_method && (
                                            <Badge color="purple">
                                                <IndianRupee className="w-3 h-3 mr-1" />
                                                {purchase.payment_method}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Info */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                            <InfoCard icon={User} label="Supplier" value={purchase.Supplier?.name || 'N/A'} highlight className="from-teal-50 to-cyan-50 border-teal-200" />
                            <InfoCard icon={Package} label="Items" value={purchase.purchase_items?.length || 0} highlight className="from-blue-50 to-indigo-50 border-blue-200" />
                            <InfoCard icon={IndianRupee} label="Total Amount" value={purchase.FormattedAmount} highlight className="from-purple-50 to-pink-50 border-purple-200" />
                            <InfoCard icon={Calendar} label="Received" value={purchase.received_date ? new Date(purchase.received_date).toLocaleDateString("en-GB") : '-'} highlight className="from-orange-50 to-amber-50 border-orange-200" />
                        </div>

                        {/* Details */}
                        <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-100 overflow-hidden">
                            <div className="bg-teal-600 px-4 sm:px-6 py-3 sm:py-4">
                                <h2 className="text-base sm:text-lg font-bold text-white">Purchase Details</h2>
                            </div>
                            <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                {[
                                    { label: "Invoice Number", value: purchase.invoice_number || '-' },
                                    // { label: "Notes", value: purchase.notes || '-', multi: true },
                                    { label: "Discount", value: purchase.FormattedDiscount },
                                    { label: "GST Amount", value: purchase.FormattedGst },
                                    { label: "Status", value: purchase.purchase_status, badge: getStatusColor(purchase.purchase_status) },
                                ].map((item, i) => (
                                    <div key={i} className="space-y-1">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
                                        {item.badge ? (
                                            <Badge color={item.badge}>{item.value}</Badge>
                                        ) : (
                                            <p className={`text-gray-900 font-medium ${item.multi ? 'whitespace-pre-wrap' : ''}`}>{item.value}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Purchased Items */}
                        {purchase.purchase_items?.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-100 overflow-hidden">
                                <div className="bg-teal-600 px-4 py-3 sm:py-4">
                                    <h2 className="text-base sm:text-lg font-bold text-white">
                                        Purchased Items ({purchase.purchase_items.length})
                                    </h2>
                                </div>

                                {/* Mobile Card View */}
                                <div className="block lg:hidden p-4 space-y-3">
                                    {purchase.purchase_items.map((item, i) => (
                                        <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:bg-teal-50 transition-all">
                                            <div className="mb-2">
                                                <div className="font-bold text-gray-900 text-sm">{item.medicine?.name || 'Unknown'}</div>
                                                {item.medicine?.generic_name && (
                                                    <div className="text-xs text-gray-500">{item.medicine.generic_name}</div>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                                <div><div className="text-xs text-gray-500 uppercase">Batch</div><div className="font-medium">{item.batch_number}</div></div>
                                                <div className="text-right"><div className="text-xs text-gray-500 uppercase">Qty</div><div className="font-bold text-teal-600">{item.quantity}</div></div>
                                                <div><div className="text-xs text-gray-500 uppercase">Price/Pkg</div><div className="font-medium">₹{parseFloat(item.price_per_package).toFixed(2)}</div></div>
                                                <div className="text-right"><div className="text-xs text-gray-500 uppercase">Total</div><div className="font-bold text-teal-600">₹{parseFloat(item.total_price).toFixed(2)}</div></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Desktop Table View */}
                                <div className="hidden lg:block overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-green-100 border-b">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Medicine</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Batch</th>
                                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Qty</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Price/Pkg</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {purchase.purchase_items.map((item, i) => (
                                                <tr key={i} className="hover:bg-teal-50 transition-colors">
                                                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                                        <div>
                                                            <div className="font-semibold">{item.medicine?.name || 'Unknown'}</div>
                                                            {item.medicine?.generic_name && (
                                                                <div className="text-xs text-gray-500">{item.medicine.generic_name}</div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{item.batch_number}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 text-center font-medium">{item.quantity}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700 text-right">₹{parseFloat(item.price_per_package).toFixed(2)}</td>
                                                    <td className="px-4 py-3 text-sm font-semibold text-teal-600 text-right">₹{parseFloat(item.total_price).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={onClose}
                                className="w-full sm:w-auto px-6 sm:px-8 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm sm:text-base font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Authenticated>
            {/* View Modal */}
            {showViewModal && selectedPurchase && (
                <PurchaseViewModal purchase={selectedPurchase} onClose={() => setShowViewModal(false)} />
            )}

            <div className="min-h-screen bg-gray-50 py-6 px-2 sm:px-4 md:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">

                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center space-x-3">
                                <div className="bg-teal-600 p-3 rounded-xl shadow-md">
                                    <ShoppingCart className="h-8 w-8 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Purchase Management</h1>
                                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Manage and track all purchases</p>
                                </div>
                            </div>
                            <Link to="/new-purchase">
                                <button className="flex items-center justify-center px-5 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200">
                                    <Plus size={20} className="mr-2" />
                                    <span>Add New Purchase</span>
                                </button>
                            </Link>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                        <div className="flex flex-col lg:flex-row gap-4 items-center">
                            {/* Search */}
                            <div className="relative flex-1 w-full min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    className="pl-10 w-full py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600 transition-all"
                                    placeholder="Search invoice..."
                                    value={searchTerm}
                                    onChange={handleFilterChange(setSearchTerm)}
                                />
                            </div>

                            {/* Supplier Filter */}
                            <div className="flex-1 w-full min-w-[200px]">
                                <select
                                    className="w-full py-2.5 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600 transition-all"
                                    value={supplierFilter}
                                    onChange={(e) => setSupplierFilter(e.target.value)}
                                >
                                    <option value="">All Suppliers</option>
                                    {suppliers.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Date Filter */}
                            <div className="flex-1 w-full min-w-[150px]">
                                <input
                                    type="date"
                                    className="w-full py-2.5 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600 transition-all"
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                />
                            </div>

                            {/* Type Filter */}
                            <div className="flex-1 w-full min-w-[150px]">
                                <select
                                    className="w-full py-2.5 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600 transition-all"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <option value="">All Types</option>
                                    <option value="REGULAR">Regular Purchase</option>
                                    <option value="OPENING_STOCK">Opening Stock</option>
                                </select>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 w-full lg:w-auto">
                                <button
                                    onClick={handleClearFilters}
                                    className="flex items-center justify-center gap-2 px-4 py-2.5 text-red-600 border border-red-200 bg-red-50 rounded-lg hover:bg-red-100 transition-all whitespace-nowrap w-full lg:w-auto"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    <span>Reset</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
                            <span className="font-medium">Error:</span> {error}
                        </div>
                    )}

                    {/* Table */}
                    {loading ? (
                        <div className="flex justify-center items-center py-16">
                            <RefreshCw className="w-10 h-10 text-teal-600 animate-spin" />
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-lg border border-teal-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
                                        <tr>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Invoice #</th>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Supplier</th>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Amount</th>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-center font-semibold uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {purchases.map((purchase) => (
                                            <tr key={purchase.id} className="hover:bg-teal-50 transition-all duration-200">
                                                <td className="px-6 py-4 font-medium text-gray-900">#{purchase.invoice_number}</td>
                                                <td className="px-6 py-4 text-gray-700">{purchase.SupplierName}</td>
                                                <td className="px-6 py-4 text-gray-700">{purchase.Purchasedate}</td>
                                                <td className="px-6 py-4 text-right font-semibold text-teal-600">{purchase.FormattedAmount}</td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${purchase.payment_status === 'PAID' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                        {purchase.payment_status || 'PENDING'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex justify-center gap-2">
                                                        <button
                                                            onClick={() => handleViewClick(purchase)}
                                                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                                                        >
                                                            <Eye size={20} />
                                                        </button>
                                                        <Link to={`/edit-purchase/${purchase.id}`}>
                                                            <button className="p-2 text-teal-600 hover:bg-teal-100 rounded-lg">
                                                                <FileEdit size={20} />
                                                            </button>
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-gray-600">
                                <div>Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} purchases</div>
                                <div className="space-x-1">
                                    <button
                                        onClick={() => setPage(page - 1)}
                                        disabled={page === 1}
                                        className={`px-4 py-2 rounded-lg transition-all ${page === 1 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPage(page + 1)}
                                        disabled={page * limit >= total}
                                        className={`px-4 py-2 rounded-lg transition-all ${page * limit >= total ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Authenticated>
    );
};

export default Purchase;